import { type EventType } from '@/lib/constants';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Event {
  eventType: EventType;
}

interface MiniEventFlowProps {
  events: Event[];
  maxDots?: number;
  showTooltip?: boolean;
}

const DOT_COLORS: Record<EventType, string> = {
  page_viewed: 'bg-slate-400',
  product_viewed: 'bg-blue-500',
  collection_viewed: 'bg-purple-500',
  search_submitted: 'bg-amber-500',
  product_added_to_cart: 'bg-green-500',
  product_removed_from_cart: 'bg-red-400',
  cart_viewed: 'bg-orange-500',
  checkout_started: 'bg-indigo-500',
  checkout_completed: 'bg-emerald-500',
  custom: 'bg-gray-400',
};

export function MiniEventFlow({ events, maxDots = 12, showTooltip = true }: MiniEventFlowProps) {
  const displayEvents = events.slice(0, maxDots);
  const hasMore = events.length > maxDots;

  const eventCounts = events.reduce((acc, e) => {
    acc[e.eventType] = (acc[e.eventType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const tooltipContent = Object.entries(eventCounts)
    .map(([type, count]) => `${count} ${type.replace(/_/g, ' ')}`)
    .join(', ');

  const content = (
    <div className="flex items-center gap-0.5">
      {displayEvents.map((event, index) => {
        const isConversion = event.eventType === 'checkout_completed';
        return (
          <span
            key={index}
            className={cn(
              'rounded-full transition-transform hover:scale-125',
              isConversion ? 'w-2.5 h-2.5 ring-2 ring-emerald-200' : 'w-1.5 h-1.5',
              DOT_COLORS[event.eventType] || 'bg-gray-400'
            )}
          />
        );
      })}
      {hasMore && (
        <span className="text-xs text-muted-foreground ml-1">+{events.length - maxDots}</span>
      )}
    </div>
  );

  if (!showTooltip) return content;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
